/**
 * Every endpoint the back office calls.
 *
 * Injected into the one API slice so there is a single cache and a single set of
 * tags. Tags are what make a saved vehicle appear in the list without a manual
 * refetch — which matters on a counter where nobody is going to press reload.
 */

import { api } from '@/app/api';
import type {
  Booking,
  BookingCharge,
  BookingChargeRequest,
  BookingPatch,
  BookingRequest,
  Calendar,
  Customer,
  CustomerPatch,
  CustomerRequest,
  Dues,
  Invoice,
  Payment,
  PaymentRequest,
  Revenue,
  Settings,
  SettingsPatch,
  Driver,
  DriverPatch,
  DriverRequest,
  Enquiry,
  EnquiryPatch,
  EnquiryRequest,
  Extra,
  ExtraRequest,
  Page,
  RateCard,
  RateCardPatch,
  RateCardRequest,
  SeasonRule,
  SeasonRuleRequest,
  Quote,
  Session,
  Today,
  TripArea,
  Vehicle,
  VehiclePatch,
  VehicleRequest,
} from './types';

/** Signing in or out changes who everything was fetched as. */
const EVERYTHING = [
  'Session',
  'Vehicle',
  'Driver',
  'RateCard',
  'Customer',
  'Payment',
  'Invoice',
  'Money',
  'Settings',
  'Enquiry',
  'Booking',
  'Availability',
  'Attention',
] as const;

export const endpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // --- Who is signed in ---------------------------------------------------
    session: build.query<Session, void>({
      query: () => 'auth/session',
      providesTags: ['Session'],
    }),
    signIn: build.mutation<Session, { phone: string; password: string }>({
      query: (body) => ({ url: 'auth/sign-in', method: 'POST', body }),
      // Everything, not just the session: whatever is in the cache was fetched
      // as somebody else — often as nobody, and refused. Two staff sharing the
      // counter machine is the normal case, not the exception.
      invalidatesTags: EVERYTHING,
    }),
    signOut: build.mutation<void, void>({
      query: () => ({ url: 'auth/sign-out', method: 'POST' }),
      // Everything cached belonged to the person who just left.
      invalidatesTags: EVERYTHING,
    }),
    changePassword: build.mutation<void, { current_password: string; new_password: string }>({
      query: (body) => ({ url: 'auth/password', method: 'POST', body }),
    }),
    changeName: build.mutation<Session, { name: string }>({
      query: (body) => ({ url: 'auth/name', method: 'POST', body }),
      invalidatesTags: ['Session'],
    }),

    // --- Vehicles -----------------------------------------------------------
    vehicles: build.query<Page<Vehicle>, { availability?: string } | void>({
      query: (args) =>
        args && args.availability
          ? `vehicles/?availability=${args.availability}`
          : 'vehicles/',
      // Also invalidated by anything that moves a booking: whether a vehicle
      // is free is a fact about the bookings, and this list now shows it.
      providesTags: ['Vehicle'],
    }),
    vehicle: build.query<Vehicle, string>({
      query: (id) => `vehicles/${id}/`,
      providesTags: ['Vehicle'],
    }),
    createVehicle: build.mutation<Vehicle, VehicleRequest>({
      query: (body) => ({ url: 'vehicles/', method: 'POST', body }),
      invalidatesTags: ['Vehicle'],
    }),
    updateVehicle: build.mutation<Vehicle, { id: string; body: VehiclePatch }>({
      query: ({ id, body }) => ({ url: `vehicles/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Vehicle'],
    }),
    deleteVehicle: build.mutation<void, string>({
      query: (id) => ({ url: `vehicles/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Vehicle', 'RateCard'],
    }),

    // --- Drivers ------------------------------------------------------------
    drivers: build.query<Page<Driver>, { includeInactive?: boolean } | void>({
      query: (args) =>
        args && args.includeInactive ? 'drivers/?include_inactive=true' : 'drivers/',
      providesTags: ['Driver'],
    }),
    driver: build.query<Driver, string>({
      query: (id) => `drivers/${id}/`,
      providesTags: ['Driver'],
    }),
    createDriver: build.mutation<Driver, DriverRequest>({
      query: (body) => ({ url: 'drivers/', method: 'POST', body }),
      invalidatesTags: ['Driver'],
    }),
    updateDriver: build.mutation<Driver, { id: string; body: DriverPatch }>({
      query: ({ id, body }) => ({ url: `drivers/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Driver'],
    }),
    deleteDriver: build.mutation<void, string>({
      query: (id) => ({ url: `drivers/${id}/`, method: 'DELETE' }),
      // Bookings too: a driver is named on them, so a list already fetched
      // would still be showing somebody who is gone.
      invalidatesTags: ['Driver', 'Booking'],
    }),

    // --- Rates --------------------------------------------------------------
    rateCards: build.query<Page<RateCard>, void>({
      query: () => 'rate-cards/',
      providesTags: ['RateCard'],
    }),
    rateCard: build.query<RateCard, string>({
      query: (id) => `rate-cards/${id}/`,
      providesTags: ['RateCard'],
    }),
    createRateCard: build.mutation<RateCard, RateCardRequest>({
      query: (body) => ({ url: 'rate-cards/', method: 'POST', body }),
      invalidatesTags: ['RateCard'],
    }),
    // Several at once, in one transaction. Four separate POSTs would leave a
    // price list half-entered when the third collided with an existing card.
    createRateCards: build.mutation<RateCard[], RateCardRequest[]>({
      query: (body) => ({ url: 'rate-cards/bulk/', method: 'POST', body }),
      invalidatesTags: ['RateCard'],
    }),
    updateRateCard: build.mutation<RateCard, { id: string; body: RateCardPatch }>({
      query: ({ id, body }) => ({ url: `rate-cards/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['RateCard'],
    }),
    seasons: build.query<Page<SeasonRule>, void>({
      query: () => 'seasons/',
      providesTags: ['RateCard'],
    }),
    createSeason: build.mutation<SeasonRule, SeasonRuleRequest>({
      query: (body) => ({ url: 'seasons/', method: 'POST', body }),
      invalidatesTags: ['RateCard'],
    }),
    // --- The counter -------------------------------------------------------
    today: build.query<Today, void>({
      query: () => 'today',
      providesTags: ['Attention', 'Booking', 'Enquiry'],
    }),
    calendar: build.query<Calendar, { from?: string; days?: number }>({
      query: ({ from, days = 14 }) =>
        `calendar?days=${String(days)}${from === undefined ? '' : `&from=${from}`}`,
      providesTags: ['Booking', 'Availability'],
    }),

    customers: build.query<
      Page<Customer>,
      { q?: string; blacklisted?: 'only' | 'exclude' } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && args.q) params.set('q', args.q);
        if (args && args.blacklisted) params.set('blacklisted', args.blacklisted);
        const query = params.toString();
        return query === '' ? 'customers/' : `customers/?${query}`;
      },
      providesTags: ['Customer'],
    }),
    customer: build.query<Customer, string>({
      query: (id) => `customers/${id}/`,
      providesTags: ['Customer'],
    }),
    createCustomer: build.mutation<Customer, CustomerRequest>({
      query: (body) => ({ url: 'customers/', method: 'POST', body }),
      invalidatesTags: ['Customer'],
    }),
    updateCustomer: build.mutation<Customer, { id: string; body: CustomerPatch }>({
      query: ({ id, body }) => ({ url: `customers/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Customer'],
    }),
    // Blacklisting invalidates bookings too: the booking form has to stop
    // offering somebody the counter has just refused.
    blacklistCustomer: build.mutation<Customer, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `customers/${id}/blacklist/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Customer', 'Booking'],
    }),
    allowCustomer: build.mutation<Customer, string>({
      query: (id) => ({ url: `customers/${id}/allow/`, method: 'POST' }),
      invalidatesTags: ['Customer', 'Booking'],
    }),

    // --- Money -------------------------------------------------------------
    //
    // Every money mutation invalidates 'Money', which is what the dues and
    // revenue screens hang off. Taking a payment has to change what those two
    // say, and a stale dues list is a customer chased for money they paid.
    // Charges added to one booking after it was taken: a scratch, a wash, a
    // changed route. They move `booking.total`, so anything showing what is
    // owed has to be refetched with them.
    bookingCharges: build.query<Page<BookingCharge>, string>({
      query: (booking) => `booking-charges/?booking=${booking}`,
      providesTags: ['Booking'],
    }),
    addBookingCharge: build.mutation<BookingCharge, BookingChargeRequest>({
      query: (body) => ({ url: 'booking-charges/', method: 'POST', body }),
      invalidatesTags: ['Booking', 'Money'],
    }),
    removeBookingCharge: build.mutation<void, string>({
      query: (id) => ({ url: `booking-charges/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Booking', 'Money'],
    }),

    payments: build.query<Page<Payment>, { booking?: string } | void>({
      query: (args) =>
        args && args.booking ? `payments/?booking=${args.booking}` : 'payments/',
      providesTags: ['Payment'],
    }),
    recordPayment: build.mutation<Payment, PaymentRequest>({
      query: (body) => ({ url: 'payments/', method: 'POST', body }),
      invalidatesTags: ['Payment', 'Money', 'Booking', 'Attention'],
    }),
    voidPayment: build.mutation<Payment, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `payments/${id}/void/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Payment', 'Money', 'Booking'],
    }),

    invoices: build.query<Page<Invoice>, { status?: string; q?: string; booking?: string } | void>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && args.status) params.set('status', args.status);
        if (args && args.q) params.set('q', args.q);
        if (args && args.booking) params.set('booking', args.booking);
        const query = params.toString();
        return query === '' ? 'invoices/' : `invoices/?${query}`;
      },
      providesTags: ['Invoice'],
    }),
    invoice: build.query<Invoice, string>({
      query: (id) => `invoices/${id}/`,
      providesTags: ['Invoice'],
    }),
    raiseInvoice: build.mutation<Invoice, { booking: string; customer_pan?: string }>({
      query: (body) => ({ url: 'invoices/raise/', method: 'POST', body }),
      invalidatesTags: ['Invoice', 'Money'],
    }),
    voidInvoice: build.mutation<Invoice, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `invoices/${id}/void/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['Invoice', 'Money'],
    }),

    dues: build.query<Dues, void>({
      query: () => 'dues',
      providesTags: ['Money'],
    }),
    // The Bikram Sambat fiscal year, or the one in progress when it is left off.
    revenue: build.query<Revenue, number | undefined>({
      query: (year) => (year === undefined ? 'revenue' : `revenue?year=${String(year)}`),
      providesTags: ['Money'],
    }),

    settings: build.query<Settings, void>({
      query: () => 'settings',
      providesTags: ['Settings'],
    }),
    updateSettings: build.mutation<Settings, SettingsPatch>({
      query: (body) => ({ url: 'settings', method: 'PATCH', body }),
      // Invoices carry the business name and PAN, so changing them changes
      // what the next document says.
      invalidatesTags: ['Settings', 'Invoice'],
    }),

    enquiries: build.query<Page<Enquiry>, { status?: string } | void>({
      query: (args) => (args && args.status ? `enquiries/?status=${args.status}` : 'enquiries/'),
      providesTags: ['Enquiry'],
    }),
    enquiry: build.query<Enquiry, string>({
      query: (id) => `enquiries/${id}/`,
      providesTags: ['Enquiry'],
    }),
    createEnquiry: build.mutation<Enquiry, EnquiryRequest>({
      query: (body) => ({ url: 'enquiries/', method: 'POST', body }),
      invalidatesTags: ['Enquiry', 'Attention'],
    }),
    updateEnquiry: build.mutation<Enquiry, { id: string; body: EnquiryPatch }>({
      query: ({ id, body }) => ({ url: `enquiries/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Enquiry'],
    }),
    markEnquiryLost: build.mutation<Enquiry, { id: string; lost_reason: string }>({
      query: ({ id, lost_reason }) => ({
        url: `enquiries/${id}/lost/`,
        method: 'POST',
        body: { lost_reason },
      }),
      invalidatesTags: ['Enquiry', 'Attention'],
    }),
    convertEnquiry: build.mutation<
      Booking,
      {
        id: string;
        vehicle?: string | null;
        driver?: string | null;
        category?: string;
        licence_no?: string;
        start_at: string;
        end_at: string;
        with_driver: boolean;
        area: TripArea;
        destination?: string;
        // Optional: left out, the server prices the trip from the rate cards,
        // which is what every conversion used to do. Sent, they win — the
        // person on the phone is the one negotiating. There is no `total`:
        // the server derives it from these two.
        subtotal?: number;
        discount?: number;
        fuel_policy?: string;
        notes?: string;
      }
    >({
      query: ({ id, ...body }) => ({ url: `enquiries/${id}/convert/`, method: 'POST', body }),
      invalidatesTags: ['Enquiry', 'Booking', 'Customer', 'Attention', 'Availability'],
    }),

    bookings: build.query<
      Page<Booking>,
      { status?: string; q?: string; sort?: string; driver?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args && args.status) params.set('status', args.status);
        if (args && args.q) params.set('q', args.q);
        if (args && args.sort) params.set('sort', args.sort);
        if (args && args.driver) params.set('driver', args.driver);
        const query = params.toString();
        return query === '' ? 'bookings/' : `bookings/?${query}`;
      },
      providesTags: ['Booking'],
    }),
    // Takes its payments and invoices with it. Manager and above.
    deleteBooking: build.mutation<void, string>({
      query: (id) => ({ url: `bookings/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Booking', 'Money', 'Invoice', 'Payment', 'Attention'],
    }),
    booking: build.query<Booking, string>({
      query: (id) => `bookings/${id}/`,
      providesTags: ['Booking'],
    }),
    createBooking: build.mutation<Booking, BookingRequest>({
      query: (body) => ({ url: 'bookings/', method: 'POST', body }),
      invalidatesTags: ['Booking', 'Availability', 'Attention'],
    }),
    updateBooking: build.mutation<Booking, { id: string; body: BookingPatch }>({
      query: ({ id, body }) => ({ url: `bookings/${id}/`, method: 'PATCH', body }),
      invalidatesTags: ['Booking', 'Availability', 'Attention'],
    }),
    transitionBooking: build.mutation<Booking, { id: string; to: string; reason?: string }>({
      query: ({ id, to, reason }) => ({
        url: `bookings/${id}/transition/`,
        method: 'POST',
        body: { to, reason },
      }),
      invalidatesTags: ['Booking', 'Availability', 'Attention'],
    }),

    quote: build.mutation<
      Quote,
      {
        vehicle?: string | null;
        category?: string;
        start_at: string;
        end_at: string;
        with_driver: boolean;
        area: TripArea;
      }
    >({
      query: (body) => ({ url: 'quote', method: 'POST', body }),
    }),
    availableVehicles: build.mutation<
      Vehicle[],
      { start_at: string; end_at: string; area: TripArea }
    >({
      query: (body) => ({ url: 'available-vehicles', method: 'POST', body }),
    }),

    extras: build.query<Page<Extra>, void>({
      query: () => 'extras/',
      providesTags: ['RateCard'],
    }),
    createExtra: build.mutation<Extra, ExtraRequest>({
      query: (body) => ({ url: 'extras/', method: 'POST', body }),
      invalidatesTags: ['RateCard'],
    }),
  }),
});

export const {
  useChangeNameMutation,
  useChangePasswordMutation,
  useSessionQuery,
  useSignInMutation,
  useSignOutMutation,
  useVehiclesQuery,
  useVehicleQuery,
  useCreateVehicleMutation,
  useUpdateVehicleMutation,
  useAddBookingChargeMutation,
  useBookingChargesQuery,
  useDeleteBookingMutation,
  useDeleteDriverMutation,
  useRemoveBookingChargeMutation,
  useDeleteVehicleMutation,
  useDriversQuery,
  useDriverQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useRateCardsQuery,
  useRateCardQuery,
  useCreateRateCardMutation,
  useCreateRateCardsMutation,
  useUpdateRateCardMutation,
  useSeasonsQuery,
  useExtrasQuery,
  useTodayQuery,
  useCalendarQuery,
  usePaymentsQuery,
  useRecordPaymentMutation,
  useVoidPaymentMutation,
  useInvoicesQuery,
  useInvoiceQuery,
  useRaiseInvoiceMutation,
  useVoidInvoiceMutation,
  useDuesQuery,
  useRevenueQuery,
  useSettingsQuery,
  useUpdateSettingsMutation,
  useCustomersQuery,
  useCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useBlacklistCustomerMutation,
  useAllowCustomerMutation,
  useEnquiriesQuery,
  useEnquiryQuery,
  useCreateEnquiryMutation,
  useUpdateEnquiryMutation,
  useMarkEnquiryLostMutation,
  useConvertEnquiryMutation,
  useBookingsQuery,
  useBookingQuery,
  useCreateBookingMutation,
  useUpdateBookingMutation,
  useTransitionBookingMutation,
  useQuoteMutation,
  useAvailableVehiclesMutation,
} = endpoints;
